import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Navbar, Nav } from 'react-bootstrap';
import SignInModal from './SignInModal';
import { trackEvent, trackPageView, trackFunnelEvent } from '../utils/analytics';

const LANDING_VIEW_SESSION_KEY = 'plum-funnel-landing-view';

function AuthPage({ user }) {
  const navigate = useNavigate();
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    trackPageView('/home');
    // Funnel step 1: a tab session ever reached the landing page. Dedup via
    // sessionStorage so a back-nav onto /home in the same tab doesn't double-fire.
    try {
      if (!sessionStorage.getItem(LANDING_VIEW_SESSION_KEY)) {
        sessionStorage.setItem(LANDING_VIEW_SESSION_KEY, String(Date.now()));
        trackFunnelEvent('landing_view', {
          referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
        });
      }
    } catch {
      // sessionStorage unavailable (private mode, SSR) — fire anyway so the
      // funnel doesn't drop the first step entirely.
      trackFunnelEvent('landing_view', {
        referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
      });
    }
  }, []);

  const handleSignIn = (source = 'unknown', extras = {}) => {
    // If already logged in, go straight to the dashboard
    if (user) {
      trackEvent('dashboard_returned', { source });
      navigate('/');
      return;
    }
    trackEvent('auth_start', { source, ...extras });
    setShowSignIn(true);
  };

  const features = [
    {
      icon: 'bi-gift',
      title: 'Completely Free',
      description: 'Plum is free for homeowners \u2014 no subscription, no credit card. Unlimited AI guidance, design generations, and a full contractor-ready project package, all at no cost.'
    },
    {
      icon: 'bi-telephone-x',
      title: 'No Spam Phone Calls',
      description: 'Share your project once. We source and evaluate vetted local contractors for you \u2014 we never hand out your phone number, so there are no robocalls and no spam.'
    },
    {
      icon: 'bi-piggy-bank',
      title: 'Save Money',
      description: 'Compare more competitive quotes against one detailed scope, and slash costly change orders by locking down every decision before work begins.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Share Your Vision',
      description: 'Tell Plum about the space you want to create. Our AI asks the right follow-up questions to capture every detail \u2014 even the ones you haven\u2019t thought of yet.'
    },
    {
      number: '2',
      title: 'Refine the Design',
      description: 'Work through materials, finishes, layouts, and specs in a guided conversation. Plum builds your project document in real time as decisions are made.'
    },
    {
      number: '3',
      title: 'Hand Off with Confidence',
      description: 'Download a complete, professional renovation package and hand it to your contractor. Every specification, every decision, all in one place.'
    }
  ];

  return (
    <div className="auth-page">
      {/* Navigation */}
      <Navbar className="landing-nav" sticky="top" expand={false}>
        <Container>
          <Navbar.Brand className="d-flex align-items-center gap-2">
            <img src="/apple-icon-180x180.png" alt="Plum" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Plum</span>
          </Navbar.Brand>
          <Nav className="d-flex align-items-center gap-3">
            <a href="/blog" className="landing-footer-link">Blog</a>
            <Button
              variant="outline-dark"
              className="landing-nav-btn"
              onClick={() => handleSignIn('nav')}
              size="sm"
            >
              {user ? 'Go to Dashboard' : 'Sign In'}
            </Button>
          </Nav>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="landing-hero">
        <Container style={{ maxWidth: 800 }}>
          <div className="text-center">
            <div className="mb-4">
              <img src="/apple-icon-180x180.png" alt="Plum" style={{ width: 80, height: 80, borderRadius: 20 }} />
            </div>
            <h1 className="landing-hero-title">
              Free Renovation Design AI<br />
              <span style={{ fontSize: '1.75rem', fontWeight: 600 }}>Design, Estimate, Find Contractors</span>
            </h1>
            <p className="landing-hero-subtitle">
              Add images of the space, colors, textures, and products to create your dream space within your budget — or just ask the AI built for expert design
            </p>

            <Button
              variant="outline-dark"
              size="lg"
              className="cta-btn d-inline-flex align-items-center justify-content-center gap-2"
              onClick={() => handleSignIn('hero')}
            >
              {user ? 'Go to Dashboard' : 'Get Started Free'}
            </Button>
            <p className="landing-hero-note mt-3">Sign in with Google or email. No credit card required.</p>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="landing-section">
        <Container>
          <h2 className="landing-section-title text-center">Why Homeowners Choose Plum</h2>
          <p className="landing-section-subtitle text-center">
            Free to use, spam-free, and built to save you money
          </p>
          <Row className="g-4 mt-2">
            {features.map((feature, idx) => (
              <Col md={4} key={idx}>
                <Card className="landing-feature-card h-100">
                  <Card.Body className="p-4 text-center">
                    <div className="landing-feature-icon mb-3">
                      <i className={`bi ${feature.icon}`}></i>
                    </div>
                    <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h5>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="landing-section landing-section-alt">
        <Container>
          <h2 className="landing-section-title text-center">How It Works</h2>
          <p className="landing-section-subtitle text-center">
            Three steps to your perfect renovation
          </p>
          <Row className="g-4 mt-2 justify-content-center">
            {steps.map((step, idx) => (
              <Col md={4} key={idx} className="text-center">
                <div className="landing-step">
                  <div className="landing-step-number">{step.number}</div>
                  <h5 className="mt-3 mb-2" style={{ color: 'var(--text-primary)' }}>{step.title}</h5>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{step.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="landing-cta">
        <Container className="text-center" style={{ maxWidth: 600 }}>
          <h2 className="landing-section-title">Start Designing Your Renovation Today</h2>
          <p className="landing-hero-subtitle">
            Join thousands of homeowners who plan smarter with Plum. Completely free, no spam calls.
          </p>
          <Button
            size="lg"
            className="cta-btn d-inline-flex align-items-center justify-content-center gap-2"
            onClick={() => handleSignIn('final-cta')}
          >
            {user ? 'Go to Dashboard' : 'Get Started'}
          </Button>
        </Container>
      </section>

      {/* Footer */}
      <footer className="landing-footer text-center">
        <Container>
          <p className="mb-2">&copy; 2026 Plum. AI-powered renovation planning.</p>
          <p className="mb-0">
            <a href="/blog" className="landing-footer-link">Blog</a>
            <span className="mx-2">|</span>
            <a href="/about" className="landing-footer-link">About</a>
            <span className="mx-2">|</span>
            <a href="/terms" className="landing-footer-link">Terms of Service</a>
            <span className="mx-2">|</span>
            <a href="/privacy" className="landing-footer-link">Privacy Policy</a>
          </p>
        </Container>
      </footer>

      <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />
    </div>
  );
}

export default AuthPage;
